var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { jsx } from "react/jsx-runtime";
import { gql, useApolloClient } from "@apollo/client";
import { createContext, useState, useMemo } from "react";
import { memoize, debounce } from "lodash";
var CartItemProductTypeInput = /* @__PURE__ */ ((CartItemProductTypeInput2) => {
  CartItemProductTypeInput2["AD_BUDGET"] = "AD_BUDGET";
  CartItemProductTypeInput2["AD_PRODUCT"] = "AD_PRODUCT";
  CartItemProductTypeInput2["AD_PRODUCT_UPGRADE"] = "AD_PRODUCT_UPGRADE";
  return CartItemProductTypeInput2;
})(CartItemProductTypeInput || {});
var ProductTypeInput = /* @__PURE__ */ ((ProductTypeInput2) => {
  ProductTypeInput2["AD_BUDGET"] = "AD_BUDGET";
  ProductTypeInput2["CATALOGUE_PRODUCT"] = "CATALOGUE_PRODUCT";
  ProductTypeInput2["JOB_AD"] = "JOB_AD";
  ProductTypeInput2["JOB_AD_EDIT"] = "JOB_AD_EDIT";
  ProductTypeInput2["JOB_AD_EXTEND"] = "JOB_AD_EXTEND";
  return ProductTypeInput2;
})(ProductTypeInput || {});
const CartFieldsFragmentDoc = gql`
    fragment CartFields on Cart {
  id
  items {
    product {
      id
      ... on AdProductCartItem {
        id
        job {
          id
          revision
        }
      }
      ... on AdProductUpgradeCartItem {
        id
        job {
          id
          revision
        }
      }
      ... on AdBudgetCartItem {
        id
      }
    }
    referenceId
    parentReferenceId
  }
  customerId
  productSetId
  promoCode
  isOrderReady
}
    `;
const TaxFieldsFragmentDoc = gql`
    fragment TaxFields on Tax {
  code
  displayRecommendation
  amount {
    value
    currency
  }
}
    `;
const PriceFieldsFragmentDoc = gql`
    fragment PriceFields on Price {
  retailPrice {
    value
    currency
  }
  netPrice {
    value
    currency
  }
  tax {
    ...TaxFields
  }
  retailTax {
    ...TaxFields
  }
  __typename
}
    ${TaxFieldsFragmentDoc}`;
const DisplayFieldsFragmentDoc = gql`
    fragment DisplayFields on ProductDisplay {
  panel {
    variant
  }
  title {
    enabled
    content
  }
  tagline {
    enabled
    content
  }
  banner {
    enabled
    type
    variant
  }
  cta {
    enabled
    url
    variant
  }
  custom {
    enabled
    content {
      id
    }
  }
  inclusions {
    enabled
    variant
    content {
      content
      helpTip {
        content
        image {
          description
          id
        }
      }
    }
  }
  image {
    enabled
    description
    id
  }
  price {
    enabled
  }
  expectations {
    enabled
    variant
  }
}
    `;
const AddItemToCartDocument = gql`
    mutation AddItemToCart($input: AddItemToCartInput!) {
  addItemToCart(input: $input) {
    ... on AddItemToCartSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on AddItemToCartErrorPayload {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const CreateOrderWithPromotionDocument = gql`
    mutation CreateOrderWithPromotion($input: CreateOrderInput!) {
  createOrderWithPromotion(input: $input) {
    ... on PromotionInvalidError {
      message
    }
    ... on CreateOrderSuccessPayload {
      order {
        orderId
        orderNumber
      }
    }
  }
}
    `;
const GetCartDocument = gql`
    query GetCart($cartId: ID!) {
  cart(cartId: $cartId) {
    ...CartFields
  }
}
    ${CartFieldsFragmentDoc}`;
const RemoveCartItemDocument = gql`
    mutation RemoveCartItem($input: RemoveCartItemInput!) {
  removeCartItem(input: $input) {
    ... on RemoveCartItemSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on CartNotFoundError {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const RemoveCartPromoCodeDocument = gql`
    mutation RemoveCartPromoCode($input: RemoveCartPromoCodeInput!) {
  removeCartPromoCode(input: $input) {
    ... on RemoveCartPromoCodeSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on CartNotFoundError {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const UpdateCartProductSetDocument = gql`
    mutation UpdateCartProductSet($input: UpdateCartProductSetInput!) {
  updateCartProductSet(input: $input) {
    ... on UpdateCartProductSetSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on UpdateCartProductSetErrorPayload {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const UpdateCartPromoCodeDocument = gql`
    mutation UpdateCartPromoCode($input: UpdateCartPromoCodeInput!) {
  updateCartPromoCode(input: $input) {
    ... on UpdateCartPromoCodeSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on UpdateCartPromoCodeErrorPayload {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const UpdateJobForCartItemByReferenceDocument = gql`
    mutation UpdateJobForCartItemByReference($input: UpdateJobForCartItemByReferenceInput!) {
  updateJobForCartItemByReference(input: $input) {
    ... on UpdateJobForCartItemByReferenceSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on CartNotFoundError {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
gql`
    query GetAdProductsForAdPosting($input: AdProductsForAdPostingInput!, $draftId: String) {
  adProductsForAdPosting(input: $input) {
    ... on ProductSetExpiredError {
      message
    }
    ... on ProductSetNotFoundError {
      message
    }
    ... on ProductList {
      display {
        format
        sectionOrder
        productCap {
          products
        }
        sortProductIds
      }
      products {
        id
        name
        __typename
        price {
          __typename
          ... on HiddenPrice {
            priceVisibility
          }
          ...PriceFields
        }
        ... on AdProduct {
          pricing {
            ... on CappedUnitPrice {
              type
              maxBillableUnits
              unitPrice {
                ... on HiddenPrice {
                  priceVisibility
                }
                ...PriceFields
              }
              cappedPrice {
                ... on HiddenPrice {
                  priceVisibility
                }
                ...PriceFields
              }
              metadata {
                unitType
              }
            }
            __typename
          }
          display {
            ...DisplayFields
          }
          displayMetadata {
            name
            tagline
            redirectUrl
            sellingPoints {
              name
              isHighlighted
              helpTip {
                content
                image {
                  url
                  altText
                }
              }
            }
            image {
              url
              description
            }
            priceDescription
          }
          upgradePaths {
            upgradeProduct {
              __typename
              id
              name
              price {
                __typename
                ... on HiddenPrice {
                  priceVisibility
                }
                ...PriceFields
              }
              display {
                ...DisplayFields
              }
              displayMetadata {
                name
                tagline
                redirectUrl
                sellingPoints {
                  name
                  isHighlighted
                  helpTip {
                    content
                    image {
                      url
                      altText
                    }
                  }
                }
                image {
                  url
                  description
                }
                priceDescription
              }
            }
            targetProduct2 {
              __typename
              id
              name
              ... on AdProduct {
                displayMetadata {
                  name
                }
              }
              ... on AdProductUpgrade {
                displayMetadata {
                  name
                }
              }
            }
            type
          }
          features {
            __typename
            ... on PerUnitBillingFeature {
              unit
            }
          }
          expectationForDraft(draftId: $draftId) {
            applicationsCompleted
            correlationId
          }
        }
      }
    }
  }
}
    ${PriceFieldsFragmentDoc}
${DisplayFieldsFragmentDoc}`;
const CreateProductSetForAdPostingDocument = gql`
    mutation CreateProductSetForAdPosting($input: CreateProductSetForAdPostingInput!) {
  createProductSetForAdPosting(input: $input) {
    productSet {
      id
    }
  }
}
    `;
const CreateProductSetForAdUpgradePurchaseDocument = gql`
    mutation CreateProductSetForAdUpgradePurchase($input: CreateProductSetForAdUpgradePurchaseInput!) {
  createProductSetForAdUpgradePurchase(input: $input) {
    productSet {
      id
    }
  }
}
    `;
const CreateProductSetForMultiLocationDocument = gql`
    mutation CreateProductSetForMultiLocation($input: CreateProductSetForMultiLocationInput!) {
  createProductSetForMultiLocation(input: $input) {
    productSet {
      id
    }
  }
}
    `;
const createCart = ({
  apolloClient,
  logger
}) => {
  const mockCart = {
    id: "mock-cart-id",
    items: [
      {
        product: {
          id: "78934543212",
          job: {
            id: "forester-1",
            revision: "forester-12"
          }
        },
        referenceId: "forester-2",
        parentReferenceId: null
      },
      {
        product: {
          id: "78934543213",
          job: {
            id: "forester-1",
            revision: "forester-12"
          }
        },
        referenceId: "forester-3",
        parentReferenceId: null
      },
      {
        product: {
          id: "78934543214"
        },
        referenceId: "forester-456",
        parentReferenceId: null
      }
    ],
    customerId: "60037828",
    productSetId: "12121",
    isOrderReady: false
  };
  return {
    // TODO: Update all error handling with error object and handle it by consumer, eg. shoppingCart
    getCart: async (request) => {
      const { data } = await apolloClient.query({
        query: GetCartDocument,
        variables: { cartId: request.cartId },
        fetchPolicy: "no-cache"
      });
      logger.info("ShoppingCart | Cart | getCart | Request completed", {
        request,
        data
      });
      return data.cart;
    },
    updateCartProductSet: async (request) => {
      const { cartId, productSetId, reason } = request;
      if (reason === "JobInputChanged") {
        const { data } = await apolloClient.mutate({
          mutation: UpdateCartProductSetDocument,
          variables: { input: { cartId, productSetId } }
        });
        logger.info(
          "ShoppingCart | Cart | updateCartProductSet | Request completed",
          {
            request,
            data
          }
        );
        return data.updateCartProductSet.cart;
      }
      return mockCart;
    },
    addCartItem: async (request) => {
      const { cartId, item } = request;
      const { data } = await apolloClient.mutate({
        mutation: AddItemToCartDocument,
        variables: { input: { cartId, item } }
      });
      logger.info("ShoppingCart | Cart | addCartItem | Request completed", {
        request,
        data
      });
      return data.addItemToCart.cart;
    },
    removeCartItem: async (request) => {
      const { cartId, referenceId } = request;
      const { data } = await apolloClient.mutate({
        mutation: RemoveCartItemDocument,
        variables: { input: { cartId, referenceId } }
      });
      logger.info("ShoppingCart | Cart | removeCartItem | Request completed", {
        request,
        data
      });
      return data.removeCartItem.cart;
    },
    updateCartItem: async (request) => {
      const { cartId, item } = request;
      const { data } = await apolloClient.mutate({
        mutation: UpdateJobForCartItemByReferenceDocument,
        variables: { input: { cartId, item } }
      });
      logger.info("ShoppingCart | Cart | updateCartItem | Request completed", {
        request,
        data
      });
      return data.updateJobForCartItemByReference.cart;
    },
    updateCartPromoCode: async (request) => {
      const { cartId, promoCode } = request;
      const { data } = await apolloClient.mutate({
        mutation: UpdateCartPromoCodeDocument,
        variables: { cartId, promoCode }
      });
      logger.info(
        "ShoppingCart | Cart | updateCartPromoCode | Request completed",
        {
          request,
          data
        }
      );
      return data.updateCartPromoCode.cart;
    },
    removeCartPromoCode: async (request) => {
      const { cartId } = request;
      const { data } = await apolloClient.mutate({
        mutation: RemoveCartPromoCodeDocument,
        variables: { cartId }
      });
      logger.info(
        "ShoppingCart | Cart | removeCartPromoCode | Request completed",
        {
          request,
          data
        }
      );
      return data.removeCartPromoCode.cart;
    }
  };
};
const delay = () => new Promise(
  (resolve) => setTimeout(resolve, Math.floor(Math.random() * 1e3) + 200)
  // 200ms - 1.2s delay
);
const createMockCart = (_apolloClient, _logger) => {
  let cart = {
    id: "mock-cart-id",
    customerId: "example",
    isOrderReady: false,
    items: []
  };
  return {
    getCart: async (_request) => {
      await delay();
      return cart;
    },
    updateCartProductSet: async (request) => {
      await delay();
      cart.productSetId = request.productSetId;
      return cart;
    },
    addCartItem: async (request) => {
      await delay();
      const items = (cart == null ? void 0 : cart.items) ?? [];
      const itemIndex = items.findIndex(
        (item) => item.referenceId === request.item.referenceId
      );
      const lineItem = {
        product: { id: request.item.product.id },
        parentReferenceId: request.item.parentReferenceId,
        referenceId: request.item.referenceId
      };
      if (itemIndex !== -1) {
        items[itemIndex] = lineItem;
      } else {
        items.push(lineItem);
      }
      cart = { ...cart, items };
      return cart;
    },
    removeCartItem: async (request) => {
      await delay();
      cart = {
        ...cart,
        items: [
          ...cart.items.filter(
            (item) => item.referenceId !== request.referenceId
          ) ?? []
        ]
      };
      return cart;
    },
    updateCartItem: async (request) => {
      await delay();
      const items = (cart == null ? void 0 : cart.items) ?? [];
      const itemIndex = items.findIndex(
        (item) => item.referenceId === request.item.referenceId
      );
      const lineItem = {
        product: {
          id: request.item.product.id,
          job: {
            id: request.item.product.job.id,
            revision: request.item.product.job.revision
          }
        },
        parentReferenceId: request.item.parentReferenceId,
        referenceId: request.item.referenceId
      };
      if (itemIndex !== -1) {
        items[itemIndex] = lineItem;
      }
      cart = { ...cart, items };
      return cart;
    },
    updateCartPromoCode: async (request) => {
      await delay();
      cart.promoCode = request.promoCode;
      return cart;
    },
    removeCartPromoCode: async (_request) => {
      await delay();
      delete cart.promoCode;
      return cart;
    }
  };
};
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  if (!getRandomValues) {
    getRandomValues = typeof crypto !== "undefined" && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);
    if (!getRandomValues) {
      throw new Error("crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported");
    }
  }
  return getRandomValues(rnds8);
}
const byteToHex = [];
for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 256).toString(16).slice(1));
}
function unsafeStringify(arr, offset = 0) {
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + "-" + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + "-" + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + "-" + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + "-" + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}
const randomUUID = typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID.bind(crypto);
const native = {
  randomUUID
};
function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }
  options = options || {};
  const rnds = options.random || (options.rng || rng)();
  rnds[6] = rnds[6] & 15 | 64;
  rnds[8] = rnds[8] & 63 | 128;
  return unsafeStringify(rnds);
}
const getCreateOrderResultFromData = (data) => {
  var _a, _b;
  if (((_a = data == null ? void 0 : data.createOrderWithPromotion) == null ? void 0 : _a.__typename) === "CreateOrderSuccessPayload" && (data == null ? void 0 : data.createOrderWithPromotion.order)) {
    return {
      result: "success",
      orderId: data.createOrderWithPromotion.order.orderId
    };
  } else if (((_b = data == null ? void 0 : data.createOrderWithPromotion) == null ? void 0 : _b.__typename) === "PromotionInvalidError") {
    return {
      result: "failure",
      error: {
        type: "PromoCodeTypeError",
        message: data.createOrderWithPromotion.message
      }
    };
  }
};
const getCreateOrderResultFromErrors = (errors) => {
  var _a;
  const error = errors == null ? void 0 : errors[0];
  if (error === void 0) {
    return;
  }
  switch ((_a = error.extensions) == null ? void 0 : _a.code) {
    case "PRODUCT_SET_EXPIRED":
    case "PRODUCT_SET_NOT_FOUND": {
      return {
        result: "failure",
        error: {
          ...error,
          type: "ProductTypeError"
        }
      };
    }
    default:
      return {
        result: "failure",
        error: {
          ...error,
          type: "OtherTypeError"
        }
      };
  }
};
const toCreateOrderResult = ({
  data,
  errors
}) => {
  const resultFromErrors = getCreateOrderResultFromErrors(errors);
  const resultFromData = getCreateOrderResultFromData(data);
  if (resultFromData) {
    return resultFromData;
  }
  if (resultFromErrors) {
    return resultFromErrors;
  }
};
const isAdProduct = (cartItemProduct) => cartItemProduct.__typename === "AdProductCartItem" || cartItemProduct.__typename === "AdProductUpgradeCartItem";
const mapCartLineItemToOrderLineItem = (cartItem, productSetId) => {
  var _a, _b;
  return {
    productAttributes: {
      catalogueProduct: {
        productId: cartItem.product.id,
        productSetId,
        jobId: isAdProduct(cartItem.product) ? (_a = cartItem.product.job) == null ? void 0 : _a.id : void 0,
        revisionId: isAdProduct(cartItem.product) ? (_b = cartItem.product.job) == null ? void 0 : _b.revision : void 0
      }
    },
    productType: ProductTypeInput.CATALOGUE_PRODUCT
  };
};
const createOrderModule = ({
  apolloClient,
  logger
}) => ({
  createOrder: async (request) => {
    const { data, errors } = await apolloClient.mutate({
      mutation: CreateOrderWithPromotionDocument,
      variables: {
        input: {
          advertiserNation: "TEST",
          // TODO to be removed
          orderCreationId: v4(),
          lineItems: request.lineItems.map(
            (item) => mapCartLineItemToOrderLineItem(item, request.productSetId)
          ),
          promoCode: request.promoCode
        }
      }
    });
    if (errors) {
      logger.error("ShoppingCart | Order | getOrder | Error getting order", {
        errors
      });
    }
    return toCreateOrderResult({ data, errors });
  }
});
class BadRequestError extends Error {
  constructor(message) {
    super(`Failed to request: ${message}`);
    __publicField(this, "errorCode", "BAD_REQUEST_ERROR");
    this.name = "BadRequestError";
  }
}
class UnrecoverableError extends Error {
  constructor(message) {
    super(`Failed to recover: ${message}`);
    __publicField(this, "errorCode", "UNRECOVERABLE_ERROR");
    this.name = "UnrecoverableError";
  }
}
const createProductSet = (client, spec, abortController) => async (data) => {
  var _a;
  const { signal } = abortController;
  const response = await client.mutate({
    mutation: spec.documentNode,
    variables: spec.getVariables(data),
    context: {
      ...client.defaultContext,
      fetchOptions: {
        signal
      }
    }
  });
  if (!response || !response.data) {
    throw new UnrecoverableError(
      "No data returned from createProductSet mutation"
    );
  }
  return (_a = response.data) == null ? void 0 : _a[spec.responseKey].productSet;
};
const CREATE_PRODUCT_STRATEGIES = {
  CreateProductSetForAdPosting: {
    responseKey: "createProductSetForAdPosting",
    documentNode: CreateProductSetForAdPostingDocument,
    getVariables: (data) => ({
      input: {
        advertiserNation: data.advertiserNation,
        draftId: data.draftId,
        jobAdDetails: {
          locationId: data.locationId,
          roleTitle: data.roleTitle,
          subClassificationId: data.subClassificationId
        },
        locale: data.locale,
        userIntent: data.userIntent
      }
    }),
    getShouldSkip: (data) => {
      const hasAllData = Boolean(
        data && data.roleTitle && data.locationId && data.subClassificationId
      );
      return !hasAllData;
    }
  },
  CreateProductSetForAdUpgrade: {
    responseKey: "createProductSetForAdUpgradePurchase",
    documentNode: CreateProductSetForAdUpgradePurchaseDocument,
    getVariables: (data) => ({
      input: {
        jobId: data.jobId,
        locale: data.locale,
        opportunityId: data.opportunityId,
        userIntent: data.userIntent
      }
    }),
    getShouldSkip: (data) => {
      const hasAllData = Boolean(data && data.jobId);
      return !hasAllData;
    }
  },
  CreateProductSetForMultiLocation: {
    responseKey: "createProductSetForMultiLocation",
    documentNode: CreateProductSetForMultiLocationDocument,
    getVariables: (data) => ({
      input: {
        draftId: data.draftId,
        jobAdDetails: {
          locationId: data.locationId,
          roleTitle: data.roleTitle,
          subClassificationId: data.subClassificationId
        },
        jobId: data.jobId,
        locale: data.locale
      }
    }),
    getShouldSkip: (data) => {
      const hasAllData = Boolean(
        data && data.roleTitle && data.locationId && data.subClassificationId && data.jobId
      );
      return !hasAllData;
    }
  }
};
const getCreateProductSetStrategy = (createProductSetId) => {
  const strategy = CREATE_PRODUCT_STRATEGIES[createProductSetId];
  if (!strategy) {
    throw new BadRequestError(
      "getCreateProductSetStrategy: Invalid product set ID"
    );
  }
  return strategy;
};
class ProductSetError extends Error {
  constructor(message) {
    super(`Product set error: ${message}`);
    __publicField(this, "errorCode", "PRODUCT_SET_ERROR");
    this.name = "ProductSetError";
  }
}
const isValidProductSetQueryResponse = (key, data) => data.hasOwnProperty(key);
const isProductSetResponseInvalid = (products) => {
  if (products && (products.__typename === "ProductSetExpiredError" || products.__typename === "ProductSetNotFoundError")) {
    return true;
  }
  return false;
};
const isProductSetError = (error) => {
  if (typeof error === "object" && error !== null && "name" in error) {
    return error.name === "ProductSetError";
  }
  return false;
};
const isBadRequestError = (error) => {
  if (typeof error === "object" && error !== null && "name" in error) {
    return error.name === "BadRequestError";
  }
  return false;
};
const BAD_REQUEST_RESPONSE = {
  loading: false,
  error: new BadRequestError("Bad request to get product set"),
  data: void 0
};
const UNRECOVERABLE_ERROR_RESPONSE = {
  loading: false,
  error: new UnrecoverableError("Failed to get product set"),
  data: void 0
};
const SKIPPED_RESPONSE = {
  loading: true,
  error: void 0,
  data: void 0
};
const getProductSet = async ({
  productSetId,
  fieldName,
  query
}) => {
  const skip = !productSetId;
  if (skip) {
    return SKIPPED_RESPONSE;
  }
  const { loading, error, data } = await query(productSetId);
  if (!isValidProductSetQueryResponse(fieldName, data)) {
    throw new BadRequestError("Bad request to get product set");
  }
  const productsResponse = data[fieldName];
  if (isProductSetResponseInvalid(productsResponse)) {
    throw new ProductSetError(productsResponse.message);
  }
  return {
    loading,
    data: productsResponse,
    error
  };
};
const createProduct = ({
  apolloClient,
  createProductSetId,
  logger,
  onCreateProductSetId,
  onCreateProductSetError
}) => {
  const spec = getCreateProductSetStrategy(createProductSetId);
  const memoizedCreateProductSet = memoize(() => {
    let currentController = null;
    return (data) => {
      if (currentController) {
        currentController.abort();
      }
      currentController = new AbortController();
      return createProductSet(apolloClient, spec, currentController)(data);
    };
  });
  const createProductSetRequest = async (data, reason) => {
    if (!spec) {
      throw new BadRequestError(
        `invalid createProductSetId: ${createProductSetId}`
      );
    }
    if (spec.getShouldSkip(data)) {
      logger.info("Skipping createProductSet due to missing request data", {
        data
      });
      return;
    }
    try {
      const productSet = await memoizedCreateProductSet()(data);
      onCreateProductSetId(productSet.id, reason);
    } catch (error) {
      onCreateProductSetError(
        new UnrecoverableError("Failed to create product set")
      );
    }
  };
  const debouncedCreateProductSet = debounce(createProductSetRequest, 200);
  return {
    // Creates a new product set using the injected method from the host application
    createProductSet: async (data, reason) => {
      await debouncedCreateProductSet(data, reason);
    },
    // Loads the display based products for a given product set id
    getProductSet: async ({
      productSetId,
      fieldName,
      query
    }) => {
      try {
        return await getProductSet({
          productSetId,
          fieldName,
          query
        });
      } catch (error) {
        if (isProductSetError(error)) {
          throw error;
        }
        if (isBadRequestError(error)) {
          return BAD_REQUEST_RESPONSE;
        }
        return UNRECOVERABLE_ERROR_RESPONSE;
      }
    }
  };
};
const ROOT_PRODUCT_REFERENCE_ID = "product|parent";
const getReferenceIdsForProduct = (createProductSetId) => (productId, productType) => {
  switch (createProductSetId) {
    case "CreateProductSetForAdPosting":
    case "CreateProductSetForAdUpgrade":
    case "CreateProductSetForMultiLocation":
    default: {
      const isAddon = productType === "AdProductAddOn";
      const parentReferenceId = isAddon ? ROOT_PRODUCT_REFERENCE_ID : void 0;
      const referenceId = isAddon ? `addon|${productId}` : ROOT_PRODUCT_REFERENCE_ID;
      return { referenceId, parentReferenceId };
    }
  }
};
const createSubscriberStore = ({
  onProductsStateChanged,
  getProductSet: getProductSet2,
  onProductSetExpired
}) => {
  const state = {
    productSetId: null,
    subscribers: /* @__PURE__ */ new Map()
  };
  const runSubscriptionBySubscriptionKey = async (key) => {
    if (!state.productSetId) return;
    const config = state.subscribers.get(key);
    if (!config) return;
    onProductsStateChanged({
      subscriberKey: key,
      error: void 0,
      loading: true
    });
    try {
      const { loading, error, data } = await getProductSet2({
        productSetId: state.productSetId,
        query: config.operation,
        fieldName: config.fieldName
      });
      onProductsStateChanged({
        subscriberKey: key,
        error,
        loading,
        products: data,
        display: data == null ? void 0 : data.display
      });
    } catch (error) {
      if (isProductSetError(error)) {
        onProductSetExpired();
      }
    }
  };
  const subscribeForProducts = (key, fieldName, operation) => {
    state.subscribers.set(key, { fieldName, operation });
    runSubscriptionBySubscriptionKey(key);
  };
  const unsubscribeForProducts = (id) => {
    state.subscribers.delete(id);
  };
  const onProductSetIdUpdated = (productSetId) => {
    state.productSetId = productSetId;
    Array.from(state.subscribers.entries()).map(
      async ([key]) => await runSubscriptionBySubscriptionKey(key)
    );
  };
  return {
    subscribeForProducts,
    unsubscribeForProducts,
    onProductSetIdUpdated
  };
};
const createShoppingCart = ({
  apolloClient,
  createProductSetId,
  onProductsStateChanged,
  onProductSetIdUpdated,
  onCartUpdated,
  onOrderUpdated,
  onCartProductCartProgress,
  useMockCart,
  logger
}) => {
  const state = {
    subscribersForProducts: /* @__PURE__ */ new Map(),
    inputDataForProductSetCreation: {}
  };
  const cart = useMockCart ? createMockCart() : createCart({ apolloClient, logger });
  const order = createOrderModule({ apolloClient, logger });
  const CREATE_ORDER_MAX_RETRIES = 1;
  const product = createProduct({
    apolloClient,
    createProductSetId,
    logger,
    onCreateProductSetId: (productSetId, reason) => {
      if (state.cartId) {
        cart.updateCartProductSet({
          cartId: state.cartId,
          productSetId,
          reason
        });
      }
      subscribers.onProductSetIdUpdated(productSetId);
      onProductSetIdUpdated(productSetId);
    },
    onCreateProductSetError: (error) => {
      logger.error(
        "ShoppingCart | Product | createProductSetError | Error creating product set",
        {
          error
        }
      );
    }
  });
  const subscribers = createSubscriberStore({
    onProductsStateChanged,
    getProductSet: product.getProductSet,
    onProductSetExpired: () => {
      product.createProductSet(
        state.inputDataForProductSetCreation,
        "ProductSetExpiry"
      );
    }
  });
  const updateInputDataForProductSetCreation = (value) => {
    state.inputDataForProductSetCreation = value;
    product.createProductSet(
      state.inputDataForProductSetCreation,
      "JobInputChanged"
    );
  };
  const mapProductTypeToCartProductType = (productType) => {
    switch (productType) {
      case "AdProduct":
        return CartItemProductTypeInput.AD_PRODUCT;
      case "AdProductUpgrade":
        return CartItemProductTypeInput.AD_PRODUCT_UPGRADE;
      default:
        throw new Error(`Unknown product type: ${productType}`);
    }
  };
  const getCart = async (cartId) => await cart.getCart({ cartId });
  const getCreateOrderRequest = async (cartId) => {
    const cartData = await getCart(cartId);
    if (!(cartData == null ? void 0 : cartData.isOrderReady)) {
      logger.error(
        `ShoppingCart | Product | createProductSetError | Cart data invalid`,
        { cartData }
      );
      return;
    }
    return {
      lineItems: cartData.items,
      productSetId: cartData.productSetId,
      promoCode: cartData.promoCode ?? void 0
    };
  };
  const removePromoCode = async (cartId) => {
    const newCartData = await cart.removeCartPromoCode({ cartId });
    await onCartUpdated(newCartData);
  };
  const createOrderHandler = async (cartId, retry = 0) => {
    const createdOrderRequest = await getCreateOrderRequest(cartId);
    if (!createdOrderRequest) {
      return;
    }
    const response = await order.createOrder(createdOrderRequest);
    if (response === void 0) {
      onOrderUpdated({
        order: void 0,
        loading: true,
        productAlert: void 0,
        createOrderFatalError: void 0
      });
      return;
    }
    if (response.result === "success") {
      onOrderUpdated({
        order: {
          orderId: response.orderId
        },
        loading: false,
        productAlert: void 0,
        createOrderFatalError: void 0
      });
      return;
    }
    if (response.error.type === "PromoCodeTypeError") {
      return await removePromoCode(cartId);
    }
    if (response.error.type === "ProductTypeError" && retry < CREATE_ORDER_MAX_RETRIES) {
      await product.createProductSet(
        state.inputDataForProductSetCreation,
        "ProductSetExpiry"
      );
      return await createOrderHandler(cartId, retry + 1);
    }
    if (response.error.type === "OtherTypeError") {
      logger.error(
        "ShoppingCart | CreateShoppingCart | createOrderHandler | OtherTypeError",
        {
          createOrderError: response.error
        }
      );
      return onOrderUpdated({
        order: void 0,
        loading: false,
        productAlert: void 0,
        createOrderFatalError: response.error.message
      });
    }
  };
  return {
    // Cart Public Methods
    addLineItem: async ({ productId, productType }) => {
      const { referenceId, parentReferenceId } = getReferenceIdsForProduct(
        createProductSetId
      )(productId, productType);
      onCartProductCartProgress("start", productId);
      if (state.cartId) {
        const cartItemProductType = mapProductTypeToCartProductType(productType);
        const result = await cart.addCartItem({
          cartId: state.cartId,
          item: {
            product: { id: productId, productType: cartItemProductType },
            parentReferenceId,
            referenceId
          }
        });
        onCartUpdated(result);
      } else {
        logger.error(
          "ShoppingCart | Product | addCartItem | Cart ID is not set, unable to add cart item",
          {}
        );
      }
      onCartProductCartProgress("finish", productId);
    },
    removeLineItem: async ({ productId, productType }) => {
      const { referenceId } = getReferenceIdsForProduct(createProductSetId)(
        productId,
        productType
      );
      onCartProductCartProgress("start", productId);
      if (state.cartId) {
        const result = await cart.removeCartItem({
          cartId: state.cartId,
          referenceId
        });
        onCartUpdated(result);
      } else {
        logger.error(
          "ShoppingCart | Product | removeCartItem | Cart ID is not set, unable to remove cart item",
          {}
        );
      }
      onCartProductCartProgress("finish", productId);
    },
    updateLineItem: async ({ productId, productType, job }) => {
      const { referenceId, parentReferenceId } = getReferenceIdsForProduct(
        createProductSetId
      )(productId, productType);
      if (state.cartId) {
        const result = await cart.updateCartItem({
          cartId: state.cartId,
          item: {
            parentReferenceId,
            product: {
              id: productId,
              job
            },
            referenceId
          }
        });
        onCartUpdated(result);
      } else {
        logger.error(
          "ShoppingCart | Product | updateCartItem | Cart ID is not set, unable to update cart item",
          {}
        );
      }
    },
    addPromoCode: () => {
    },
    removePromoCode: () => {
    },
    setCartId: async (cartId) => {
      state.cartId = cartId;
      const result = await cart.getCart({ cartId });
      if (result) {
        onCartUpdated(result);
        if (result.productSetId) {
          subscribers.onProductSetIdUpdated(result.productSetId);
        }
      }
    },
    createOrder: async () => await createOrderHandler(state.cartId),
    // User Input Data Methods
    updateInputDataForProductSetCreation,
    // Subscription Methods
    subscribeForProducts: subscribers.subscribeForProducts,
    unsubscribeForProducts: subscribers.unsubscribeForProducts
  };
};
const defaultLogger = {
  /* eslint-disable no-console */
  warn: console.warn,
  error: console.error,
  info: console.info
  /* eslint-enable no-console */
};
const ShoppingCartContext = createContext({
  productResponses: {},
  cartState: void 0,
  orderState: {
    order: void 0,
    loading: false,
    productAlert: void 0,
    createOrderFatalError: void 0
  },
  cartProductInProgress: {},
  productSetId: void 0,
  onApplyPromoCode: () => {
  },
  onClearPromoCode: () => {
  },
  addLineItem: () => {
  },
  removeLineItem: () => {
  },
  updateLineItem: () => {
  },
  updateInputData: () => {
  },
  subscribeForProducts: () => {
  },
  unsubscribeForProducts: () => {
  },
  setCartId: () => {
  },
  createOrder: () => {
  },
  assignJobIdAndRevisionToParentProduct: () => {
  }
});
const ShoppingCartProvider = ({
  children,
  createProductSetId,
  logger = defaultLogger,
  useMockCart = false
}) => {
  const [cartState, setCartState] = useState();
  const [orderState, setOrderState] = useState({
    order: void 0,
    loading: false,
    productAlert: void 0,
    createOrderFatalError: void 0
  });
  const [productResponses, setProductResponses] = useState({});
  const [cartProductInProgress, setCartProductInProgress] = useState({});
  const [productSetId, setProductSetId] = useState();
  const client = useApolloClient();
  const shoppingCart = useMemo(
    () => createShoppingCart({
      apolloClient: client,
      useMockCart,
      createProductSetId,
      logger,
      onProductSetIdUpdated: setProductSetId,
      onProductsStateChanged: ({ subscriberKey, ...data }) => {
        setProductResponses((current) => {
          var _a, _b;
          return {
            ...current,
            [subscriberKey]: {
              loading: data.loading,
              error: data.error,
              // Maintain existing values while loading new ones
              products: data.products ?? (data.loading ? (_a = current[subscriberKey]) == null ? void 0 : _a.products : data.products),
              display: data.display ?? (data.loading ? (_b = current[subscriberKey]) == null ? void 0 : _b.display : data.display)
            }
          };
        });
      },
      onCartUpdated: (newCartState) => setCartState(newCartState),
      onCartProductCartProgress: (progress, productId) => {
        setCartProductInProgress((current) => {
          switch (progress) {
            case "start": {
              return { ...current, [productId]: true };
            }
            case "finish": {
              const newState = { ...current };
              delete newState[productId];
              return newState;
            }
            default:
              return current;
          }
        });
      },
      onOrderUpdated: (newOrderState) => setOrderState(newOrderState)
    }),
    [client, createProductSetId, logger, useMockCart]
  );
  return /* @__PURE__ */ jsx(
    ShoppingCartContext.Provider,
    {
      value: {
        productResponses,
        cartState,
        orderState,
        cartProductInProgress,
        productSetId,
        addLineItem: shoppingCart.addLineItem,
        removeLineItem: shoppingCart.removeLineItem,
        updateLineItem: shoppingCart.updateLineItem,
        updateInputData: shoppingCart.updateInputDataForProductSetCreation,
        subscribeForProducts: shoppingCart.subscribeForProducts,
        unsubscribeForProducts: shoppingCart.unsubscribeForProducts,
        onApplyPromoCode: () => {
        },
        onClearPromoCode: () => {
        },
        setCartId: shoppingCart.setCartId,
        createOrder: shoppingCart.createOrder,
        assignJobIdAndRevisionToParentProduct: ({ jobId, revisionId }) => {
          const parentItem = cartState == null ? void 0 : cartState.items.find(
            (item) => typeof item.parentReferenceId !== "string" && item.referenceId === ROOT_PRODUCT_REFERENCE_ID
          );
          if (!parentItem) return;
          shoppingCart.updateLineItem({
            productId: parentItem == null ? void 0 : parentItem.product.id,
            // @TODO: Ideally we would get the correct type. This won't
            // matter for the short term because only "addons" causes a new reference id type
            // to be created so "ad product" or "ad product upgrade" does the same thing either way
            productType: "AdProduct",
            job: { id: jobId, revision: revisionId }
          });
        }
      },
      children
    }
  );
};
export {
  ShoppingCartContext,
  ShoppingCartProvider
};
